import CandlestickLoader from "@/components/common/CandlestickLoader";
import { SearchFilterToolbar } from "@/components/common/SearchFilterToolbar";
import { formatStrictWithApprox, parseApproxConfigFromToml } from "@/components/results/utils";
import { objectiveLabel } from "@/utils/objectiveLabel";
import { PageHeader } from "@/components/layout";
import { SystemPausedBanner } from "@/components/SystemPausedBanner";
import { useGlobalStudiesCache } from "@/hooks/useGlobalStudiesCache";
import { OrbManager } from "@/components/study-library/OrbManager";
import { StudyLabelControl } from "@/components/study-library/StudyLabelControl";
import { useSaveStudyLabel } from "@/hooks/useStudyLabel";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useEnhancedToast as useToast } from "@/components/ui/enhanced-toast";
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { Progress } from "@/components/ui/progress";
import {
  SortControl,
  type SortConfig,
} from "@/components/ui/sort-control";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";
import { SSEEvent, useModernSSE } from "@/hooks/useModernSSE";
import type {
  Study,
  StudyStatusPayload,
  TrialCompletedPayload,
} from "@/types/api";
import { TREE_CONNECTOR } from "@/utils/bezierPath";
import { cn } from "@/utils/cn";
import {
  DateFormats,
  calculateDurationSeconds,
  formatDuration,
  formatLocalDate,
  formatRelativeTime,
} from "@/utils/dateUtils";
import { logger } from "@/utils/logger";
import {
  buildStudyDisplayItems,
  parseStudyTimestamp,
  type ParallelStudyGroup,
} from "@/utils/studyGrouping";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  AlertCircle,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  Eye,
  Layers,
  Loader2,
  Pause,
  Play,
  Settings2,
  Square,
  Trash2,
  Trophy,
  WifiOff,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorStateHandler } from "../components/common/ErrorStateHandler";
import {
  NoFilteredStudiesEmptyState,
  NoStudiesEmptyState,
} from "../components/ui/empty-state";

const GROUPS_PER_PAGE = 20;

// Animation timing: 180ms card animation + 20ms buffer for DOM settle
const ANIMATION_SETTLE_DELAY_MS = 200;

const ACTIVE_STUDIES_AUTH_MESSAGES = [
  "Syncing strategist credentials",
  "Dialing into the live trial feed",
  "Spinning up SSE telemetry",
  "Escorting you to the study floor",
];

const ACTIVE_STUDIES_DATA_MESSAGES = [
  "Linking live optimization streams",
  "Tracking momentum across studies",
  "Aggregating performance telemetry",
  "Rendering progress dashboards",
];

export const getBackendStatusFilter = (
  selectedStatus: string
): string | undefined => (selectedStatus !== "all" ? selectedStatus : undefined);

// Sort studies for display. Search and status filtering are handled server-side;
// only client-side sorting is applied here for instant UI updates.
export const filterAndSortStudies = ({
  studies,
  sortConfig,
}: {
  studies: Study[];
  sortConfig: SortConfig;
}): Study[] => {
  const result = [...studies];

  result.sort((a, b) => {
    const fieldKey = sortConfig.field === "updated" ? "updated_at" : "created_at";
    const dateA = parseStudyTimestamp(a[fieldKey]) ?? 0;
    const dateB = parseStudyTimestamp(b[fieldKey]) ?? 0;
    return sortConfig.direction === "desc" ? dateB - dateA : dateA - dateB;
  });

  return result;
};

/**
 * Check if a study is "slot-waiting" - queued but not yet enqueued to Valkey.
 * Slot-waiting studies can be manually started when a slot becomes available.
 */
const isSlotWaiting = (study: Study): boolean =>
  study.status === "queued" && !study.job_id;

// LiveStudyCardWrapper receives the latest SSE event and derives live metrics
const LiveStudyCardWrapper = ({
  study,
  latestEvent,
  isLeader,
  ...props
}: Omit<StudyCardProps, "liveProgress"> & {
  latestEvent?: SSEEvent | null;
  isLeader?: boolean;
}) => {
  const liveProgressData = useMemo(() => {
    if (!latestEvent?.data) return null;

    const rawPayload =
      (latestEvent.data.payload as unknown) ??
      (latestEvent.data as Record<string, unknown>);

    const payload = (() => {
      if (typeof rawPayload === "string") {
        try {
          return JSON.parse(rawPayload) as Record<string, unknown>;
        } catch (error) {
          logger.warn("Failed to parse live payload string", error);
          return null;
        }
      }
      if (rawPayload && typeof rawPayload === "object") {
        return rawPayload as Record<string, unknown>;
      }
      return null;
    })();

    if (!payload) return null;

    if (latestEvent.event === "trial.completed") {
      const castPayload = payload as unknown as TrialCompletedPayload;
      const estimatedRemaining =
        castPayload.estimated_remaining_ms != null
          ? Number(castPayload.estimated_remaining_ms)
          : NaN;
      const throughputValue =
        castPayload.trials_per_second != null
          ? Number(castPayload.trials_per_second)
          : NaN;

      return {
        current_trial: castPayload.current_trial,
        total_trials: castPayload.total_trials,
        progress_percentage: castPayload.progress_percentage,
        estimated_remaining_ms: Number.isFinite(estimatedRemaining)
          ? estimatedRemaining
          : undefined,
        best_value: castPayload.best_value,
        trials_per_second: Number.isFinite(throughputValue)
          ? throughputValue
          : undefined,
      };
    }

    if (
      latestEvent.event === "study.status" &&
      (payload as unknown as StudyStatusPayload).status === "completed"
    ) {
      const completionSummary = (payload as unknown as StudyStatusPayload)
        .completion_summary;
      const totalTrials =
        completionSummary?.n_trials ?? (study.n_trials as number);
      const completedTrials =
        completionSummary?.n_trials_completed ?? totalTrials;

      return {
        current_trial: completedTrials,
        total_trials: totalTrials,
        progress_percentage: 100,
        estimated_remaining_ms: 0,
        best_value:
          completionSummary?.best_value ?? (study.best_value as number),
        trials_per_second: completionSummary?.trials_per_second ?? undefined,
      };
    }
    return null;
  }, [latestEvent, study.best_value, study.n_trials]);

  return <StudyCard study={study} {...props} liveProgress={liveProgressData} isLeader={isLeader} />;
};

// =============================================================================
// LiveStudyBundleCard - Renders a parallel study group as a single expandable unit
// =============================================================================

interface LiveStudyBundleCardProps {
  parallelGroup: ParallelStudyGroup;
  liveEvents: Record<string, SSEEvent>;
  onStop: (studyId: string) => void;
  onPause: (studyId: string) => void;
  onResume: (studyId: string) => void;
  onStart: (studyId: string, studyName: string) => void;
  onDelete: (studyId: string, studyName?: string) => void;
  isManageMode: boolean;
  selectedStudies: Set<string>;
  onToggleSelect: (studyId: string) => void;
  onNavigate: (path: string) => void;
  resumingStudyId: string | null; // Track which study is currently being resumed
  startingStudyId: string | null; // Track which study is currently being started
  allFrozen?: boolean;
  // Controlled expansion state - lifted to parent to persist across filter changes
  isExpanded: boolean;
  onToggleExpand: () => void;
}

/**
 * MeasuredCardWrapper - Measures a card's vertical center position relative to SVG container
 *
 * Uses ResizeObserver to track actual DOM positions, enabling dynamic SVG tree connector
 * alignment even when card heights vary (e.g., when "estimated time remaining" line appears).
 */
interface MeasuredCardWrapperProps {
  studyId: string;
  onMeasure: (studyId: string, centerY: number) => void;
  svgContainerRef: React.RefObject<SVGSVGElement | null>;
  /** Key that changes when layout should be re-measured (e.g., when filter changes) */
  layoutKey: string;
  children: React.ReactNode;
}

function MeasuredCardWrapper({
  studyId,
  onMeasure,
  svgContainerRef,
  layoutKey,
  children,
}: MeasuredCardWrapperProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  const measurePosition = useCallback(() => {
    const cardEl = cardRef.current;
    const svgEl = svgContainerRef.current;
    if (!cardEl || !svgEl) return;

    const cardRect = cardEl.getBoundingClientRect();
    const svgRect = svgEl.getBoundingClientRect();

    // Calculate card center Y relative to SVG container top
    const cardCenterY = cardRect.top + cardRect.height / 2 - svgRect.top;
    onMeasure(studyId, cardCenterY);
  }, [onMeasure, studyId, svgContainerRef]);

  useEffect(() => {
    const cardEl = cardRef.current;
    const svgEl = svgContainerRef.current;
    if (!cardEl || !svgEl) return;

    // Measure immediately to populate initial values (may be slightly off during animation)
    measurePosition();

    // Re-measure after animations settle (y: 6 → y: 0 animation takes 180ms)
    // This corrects any offset from measuring during animation
    let rafId: number;
    const timeoutId = setTimeout(() => {
      rafId = requestAnimationFrame(measurePosition);
    }, ANIMATION_SETTLE_DELAY_MS);

    // Re-measure when card resizes (handles dynamic content like ETA line)
    const observer = new ResizeObserver(measurePosition);
    observer.observe(cardEl);

    // Also re-measure on window resize (handles layout shifts)
    window.addEventListener("resize", measurePosition);

    // Re-measure on scroll (handles scroll-induced position changes)
    // Use passive listener for better scroll performance
    window.addEventListener("scroll", measurePosition, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener("resize", measurePosition);
      window.removeEventListener("scroll", measurePosition);
    };
  }, [measurePosition, layoutKey]); // Re-run when layoutKey changes (filter toggled)

  return <div ref={cardRef}>{children}</div>;
}

/**
 * Extracts and parses the payload from an SSE event.
 * Handles both nested `event.data.payload` structure and direct `event.data` structure,
 * as well as string payloads that need JSON parsing.
 */
function extractSSEPayload(
  event: SSEEvent | undefined
): Record<string, unknown> | null {
  if (!event?.data) return null;

  const rawPayload =
    (event.data.payload as unknown) ?? (event.data as Record<string, unknown>);

  if (typeof rawPayload === "string") {
    try {
      return JSON.parse(rawPayload) as Record<string, unknown>;
    } catch {
      logger.warn("Failed to parse live payload string in bundle card");
      return null;
    }
  }
  if (rawPayload && typeof rawPayload === "object") {
    return rawPayload as Record<string, unknown>;
  }
  return null;
}

export const LiveStudyBundleCard = ({
  parallelGroup,
  liveEvents,
  onStop,
  onPause,
  onResume,
  onStart,
  onDelete,
  isManageMode,
  selectedStudies,
  onToggleSelect,
  onNavigate,
  resumingStudyId,
  startingStudyId,
  isExpanded,
  onToggleExpand,
  allFrozen,
}: LiveStudyBundleCardProps) => {
  // showExpandedContent delays content render until animation completes
  // This is separate from isExpanded (controlled by parent) for animation timing
  const [showExpandedContent, setShowExpandedContent] = useState(false);

  // Track which studies should emit golden orbs and at what tier
  // 'personal' = beat own previous best (gold color, normal size)
  // 'overall' = beat the group's best (gold color, 1.6x size, slower)
  const [goldenPending, setGoldenPending] = useState<Map<string, 'personal' | 'overall'>>(new Map());

  // Track per-study best values (for personal best detection)
  const prevBestValueRef = useRef<Map<string, number>>(new Map());

  // Track the overall best_value (Sortino ratio) across ALL studies in the parallel group
  const overallBestValueRef = useRef<number | null>(null);

  // SVG container ref for measuring card positions relative to it
  const svgContainerRef = useRef<SVGSVGElement>(null);

  // Progress bar ref for dynamic SVG positioning (eliminates hardcoded top/left values)
  const progressBarRef = useRef<HTMLDivElement>(null);

  // GlassCard ref for measuring SVG position relative to card
  const cardRef = useRef<HTMLDivElement>(null);

  // Dynamically measured progress bar position (replaces hardcoded top: 80px, left: 30px)
  // This ensures the SVG connector always starts exactly at the progress bar's left edge
  const [progressBarPosition, setProgressBarPosition] = useState<{ top: number; left: number } | null>(null);

  // Dynamically measured card center positions (Map<studyId, centerY>)
  // This replaces hardcoded pixel calculations to handle variable card heights
  const [measuredPositions, setMeasuredPositions] = useState<Map<string, number>>(new Map());

  // Create a stable key from study statuses to detect when any study status changes
  // This ensures SVG re-measures when studies complete, fail, pause, etc.
  const studyStatusKey = useMemo(
    () => parallelGroup.studies.map((s) => `${s.study_id}:${s.status}`).join('|'),
    [parallelGroup.studies]
  );

  // Measure progress bar position relative to card container
  // This runs whenever:
  // - Expanded state changes
  // - Study statuses change (studies complete, fail, etc.)
  // - Study list changes (studies added/removed)
  // - Window resizes
  // - DOM mutations occur (filter changes, layout shifts)
  //
  // CRITICAL: Start measuring as soon as isExpanded is true, NOT when showExpandedContent is true.
  // The progress bar is always in the DOM (in the header), so we can measure it immediately.
  // This ensures progressBarPosition is set BEFORE showExpandedContent becomes true,
  // avoiding a race condition where the SVG tries to render with a null position.
  useEffect(() => {
    const progressBarEl = progressBarRef.current;
    const cardEl = cardRef.current;
    if (!progressBarEl || !cardEl || !isExpanded) return;

    let rafId: number | null = null;
    let measureTimeout: ReturnType<typeof setTimeout> | null = null;

    const measureProgressBar = () => {
      // Use requestAnimationFrame for smooth, batched measurements
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const progressRect = progressBarEl.getBoundingClientRect();
        const cardRect = cardEl.getBoundingClientRect();

        // Position SVG so rail (at x=3) aligns with progress bar's left edge
        // Progress bar left relative to card = progressRect.left - cardRect.left
        // SVG left = that value - 3 (since rail is drawn at x=3 inside SVG)
        const svgLeft = progressRect.left - cardRect.left - 3;

        // Position SVG top to start flush with progress bar bottom (pixel-perfect)
        const svgTop = progressRect.bottom - cardRect.top;

        setProgressBarPosition((prev) => {
          // Only update if values changed to avoid unnecessary re-renders
          if (prev && prev.top === svgTop && prev.left === svgLeft) return prev;
          return { top: svgTop, left: svgLeft };
        });
        rafId = null;
      });
    };

    // Debounced measurement for rapid changes (e.g., filter changes)
    const debouncedMeasure = () => {
      if (measureTimeout) clearTimeout(measureTimeout);
      measureTimeout = setTimeout(measureProgressBar, 16); // ~1 frame
    };

    // Measure immediately
    measureProgressBar();

    // Re-measure after layout animations settle
    const settleTimeout = setTimeout(measureProgressBar, 50);
    const animationTimeout = setTimeout(measureProgressBar, 300); // After framer-motion animations

    // Re-measure on element resize
    const resizeObserver = new ResizeObserver(debouncedMeasure);
    resizeObserver.observe(progressBarEl);
    resizeObserver.observe(cardEl);

    // Re-measure on window resize (responsive layouts)
    window.addEventListener('resize', debouncedMeasure);

    // Re-measure on DOM mutations within the card (filter changes, status updates)
    const mutationObserver = new MutationObserver(debouncedMeasure);
    mutationObserver.observe(cardEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (measureTimeout) clearTimeout(measureTimeout);
      clearTimeout(settleTimeout);
      clearTimeout(animationTimeout);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', debouncedMeasure);
    };
  }, [isExpanded, showExpandedContent, studyStatusKey, parallelGroup.studies.length]);

  // Callback for cards to report their measured center position
  const handleCardMeasure = useCallback((studyId: string, centerY: number) => {
    setMeasuredPositions((prev) => {
      // Only update if the value has actually changed (avoid unnecessary re-renders)
      if (prev.get(studyId) === centerY) return prev;
      const next = new Map(prev);
      next.set(studyId, centerY);
      return next;
    });
  }, []);

  // Note: We no longer clear stale measurements eagerly as it caused timing issues
  // where the SVG would render with empty data before cards could re-measure.
  // Instead, we pass studyStatusKey to MeasuredCardWrapper to trigger re-measurement
  // whenever any study's status changes (completing, failing, pausing, etc.).

  // Compute aggregate progress from SSE events and track leading study
  const aggregateProgress = useMemo(() => {
    let totalTrials = 0;
    let completedTrials = 0;
    let bestValue: number | null = null;
    let leadingStudyId: string | null = null;
    // AIE-319: track the leading study's total trade count + TOML config so the
    // parent can render the approximate "real" objective value alongside strict.
    let leadingTotalTrades: number | null = null;
    let leadingTomlConfig: string | null = null;

    for (const study of parallelGroup.studies) {
      const event = liveEvents[study.study_id];
      // Use the shared payload extraction helper (handles nested payload + JSON parsing)
      const payload = extractSSEPayload(event);

      // Use SSE data if available, otherwise fall back to study data
      const studyTotal =
        (payload?.total_trials as number) ?? study.n_trials ?? 100;
      const studyCurrent =
        (payload?.current_trial as number) ?? study.n_trials_completed ?? 0;
      const studyBest = (payload?.best_value as number) ?? study.best_value;

      totalTrials += studyTotal;
      completedTrials += studyCurrent;

      if (studyBest != null && (bestValue === null || studyBest > bestValue)) {
        bestValue = studyBest;
        leadingStudyId = study.study_id;
        leadingTotalTrades = study.total_trades ?? null;
        leadingTomlConfig = study.toml_config ?? null;
      }
    }

    const percentage =
      totalTrials > 0 ? (completedTrials / totalTrials) * 100 : 0;
    return { totalTrials, completedTrials, percentage, bestValue, leadingStudyId, leadingTotalTrades, leadingTomlConfig };
  }, [parallelGroup.studies, liveEvents]);

  // Count studies by status
  const statusCounts = useMemo(() => {
    const counts = {
      running: 0,
      queued: 0,
      completed: 0,
      failed: 0,
      paused: 0,
    };
    for (const study of parallelGroup.studies) {
      const status = study.status as keyof typeof counts;
      if (status in counts) {
        counts[status]++;
      }
    }
    return counts;
  }, [parallelGroup.studies]);

  // Calculate trial counts for orb spawn detection
  // Card positions are now dynamically measured via MeasuredCardWrapper → measuredPositions
  const trialCounts = useMemo(() => {
    const counts = new Map<string, number>();

    for (const study of parallelGroup.studies) {
      // Only track trial counts for RUNNING studies with active SSE events
      // This ensures orbs only spawn for studies actively completing trials
      // Skip: queued, completed, failed, paused, cancelled
      if (study.status !== "running") {
        continue;
      }

      const event = liveEvents[study.study_id];
      const payload = extractSSEPayload(event);

      // Only add to counts if there's an SSE event with current_trial
      if (payload?.current_trial !== undefined) {
        counts.set(study.study_id, payload.current_trial as number);
      }
    }

    return counts;
  }, [parallelGroup.studies, liveEvents]);

  // Sort studies by batch_index (V1, V2, V3...) for consistent display order
  // This provides a stable, predictable order during runtime
  // Note: parallelGroup.studies comes pre-sorted by batch_index from groupParallelStudies(),
  // but we sort again to ensure consistency if that ever changes
  const sortedStudies = useMemo(() => {
    return [...parallelGroup.studies].sort((a, b) => {
      const aIndex = a.batch_index ?? 0;
      const bIndex = b.batch_index ?? 0;
      return aIndex - bIndex;
    });
  }, [parallelGroup.studies]);

  // Detect when a study achieves a new best_value (Sortino ratio)
  // Two tiers: 'personal' (beat own best) and 'overall' (beat group's best)
  useEffect(() => {
    const prevBest = prevBestValueRef.current;
    const currentOverallBest = overallBestValueRef.current;
    let newOverallBest = currentOverallBest;
    const newGolden = new Map<string, 'personal' | 'overall'>();

    for (const study of parallelGroup.studies) {
      // Only track running studies
      if (study.status !== "running") continue;

      const studyId = study.study_id;
      const event = liveEvents[studyId];
      const payload = extractSSEPayload(event);

      // Get best_value from SSE payload (trial.completed events include this)
      // Fall back to study.best_value if no SSE event yet
      const currentBestValue =
        (payload?.best_value as number | undefined) ?? study.best_value;

      if (currentBestValue == null) continue;

      const previousPersonalBest = prevBest.get(studyId);

      // Check for personal best (study beat its own previous best)
      // Only if we already had a baseline for this study (not first initialization)
      if (previousPersonalBest !== undefined && currentBestValue > previousPersonalBest) {
        newGolden.set(studyId, 'personal');
      }

      // Check for overall best (study beat the group's best) - this overrides personal
      if (currentOverallBest !== null && currentBestValue > currentOverallBest) {
        newGolden.set(studyId, 'overall'); // Override 'personal' if set
      }

      // Update per-study tracking
      prevBest.set(studyId, currentBestValue);

      // Track the maximum for overall best
      if (newOverallBest === null || currentBestValue > newOverallBest) {
        newOverallBest = currentBestValue;
      }
    }

    // Update the overall best ref
    overallBestValueRef.current = newOverallBest;

    // Add any new golden studies to the pending map
    if (newGolden.size > 0) {
      setGoldenPending((prev) => {
        const updated = new Map(prev);
        newGolden.forEach((tier, studyId) => {
          // Only upgrade tier, never downgrade (overall > personal)
          const existingTier = updated.get(studyId);
          if (!existingTier || tier === 'overall') {
            updated.set(studyId, tier);
          }
        });
        return updated;
      });
    }
  }, [parallelGroup.studies, liveEvents]);

  // Callback to clear golden status after orb is spawned
  const handleGoldenConsumed = useCallback((studyId: string) => {
    setGoldenPending((prev) => {
      const updated = new Map(prev);
      updated.delete(studyId);
      return updated;
    });
  }, []);

  // Track displayed leader separately to synchronize trophy with golden orb
  // This ensures trophy moves at the same time as (or slightly after) the orb spawns
  const [displayedLeaderId, setDisplayedLeaderId] = useState<string | null>(null);

  // Update displayed leader after golden orb state is set
  // This runs after the goldenPending useEffect, ensuring trophy and orb are in sync
  useEffect(() => {
    const newLeaderId = aggregateProgress.leadingStudyId;

    // If leader changed and new leader has a pending 'overall' orb, the orb is about to spawn
    // Update trophy at the same time
    if (newLeaderId !== displayedLeaderId) {
      // Small delay ensures the orb render happens first or simultaneously
      const timer = setTimeout(() => {
        setDisplayedLeaderId(newLeaderId);
      }, 16); // ~1 frame delay
      return () => clearTimeout(timer);
    }
  }, [aggregateProgress.leadingStudyId, displayedLeaderId]);

  // Check selections
  const selectedCount = parallelGroup.studies.filter((s) =>
    selectedStudies.has(s.study_id)
  ).length;
  const allSelected = selectedCount === parallelGroup.studies.length;

  const handleToggleAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    for (const study of parallelGroup.studies) {
      if (allSelected) {
        if (selectedStudies.has(study.study_id)) onToggleSelect(study.study_id);
      } else {
        if (!selectedStudies.has(study.study_id)) {
          onToggleSelect(study.study_id);
        }
      }
    }
  };

  const getStatusColor = () => {
    if (statusCounts.running > 0 || statusCounts.queued > 0) {
      return "from-sky-500/20 to-cyan-600/20 border-sky-500/30";
    }
    if (statusCounts.completed === parallelGroup.studies.length) {
      return "from-green-500/20 to-emerald-600/20 border-green-500/30";
    }
    if (statusCounts.failed > 0) {
      return "from-red-500/20 to-red-600/20 border-red-500/30";
    }
    return "from-slate-500/20 to-slate-600/20 border-slate-500/30";
  };

  const getStatusBadge = () => {
    if (statusCounts.running > 0) {
      return {
        label: "Running",
        className: "bg-blue-500/20 text-blue-400 border-blue-500/40",
      };
    }
    if (statusCounts.queued > 0) {
      return {
        label: "Queued",
        className: "bg-blue-500/20 text-blue-400 border-blue-500/40",
      };
    }
    if (statusCounts.completed === parallelGroup.studies.length) {
      return {
        label: "Completed",
        className: "bg-green-500/20 text-green-400 border-green-500/40",
      };
    }
    if (statusCounts.failed > 0) {
      return {
        label: "Failed",
        className: "bg-red-500/20 text-red-400 border-red-500/40",
      };
    }
    return {
      label: "Mixed",
      className: "bg-slate-500/20 text-slate-400 border-slate-500/40",
    };
  };

  const statusBadge = getStatusBadge();

  useEffect(() => {
    if (!isExpanded) {
      setShowExpandedContent(false);
    }
  }, [isExpanded]);

  return (
    <GlassCard
      ref={cardRef}
      className={cn(
        "relative transition-all duration-300",
        "overflow-hidden",
        // Only show glow on hover - removed unconditional shadow-glow
        (statusCounts.running > 0 || statusCounts.queued > 0) &&
          "hover:shadow-glow"
      )}
    >
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-r opacity-10",
          getStatusColor()
        )}
      />

      {/* Bundle Header */}
      <GlassCardHeader
        className={cn(
          "relative cursor-pointer",
          isExpanded ? "pb-2" : "pb-4"
        )}
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-3">
          {/* Expand/collapse chevron - NOW ON LEFT per mockup */}
          <button
            className="p-1 rounded hover:bg-dark-800/50 transition-colors shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
          </button>

          {/* Selection checkbox in manage mode */}
          {isManageMode && (
            <button
              onClick={handleToggleAll}
              className="p-1 rounded hover:bg-dark-800/50 transition-colors shrink-0"
            >
              {allSelected ? (
                <CheckSquare className="h-5 w-5 text-brand-blue" />
              ) : (
                <Square className="h-5 w-5 text-muted-foreground" />
              )}
            </button>
          )}

          {/* Bundle icon with count beside it */}
          <div className="flex items-center gap-0.5 shrink-0">
            <Layers className="h-5 w-5 text-blue-400" />
            <span className="text-[10px] font-semibold text-blue-400/80">
              x{parallelGroup.parallel_total}
            </span>
          </div>

          {/* Title */}
          <div className="flex-1 min-w-0">
            <GlassCardTitle className="text-xl font-bold tracking-tight">
              {parallelGroup.baseName
                .replace(/^sample_/, "")
                .toUpperCase()
                .replace(/_/g, " ")}
            </GlassCardTitle>
          </div>

          {/* Right side: Status badge & best value */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Status badge */}
            <span
              className={cn(
                "px-2 py-1 rounded-full text-xs font-medium border",
                statusBadge.className
              )}
            >
              {statusBadge.label}
            </span>

            {/* Best value */}
            {aggregateProgress.bestValue != null && (
              <div className="flex items-center gap-1">
                <Trophy className="h-4 w-4 text-yellow-500" />
                <span className="font-mono text-sm text-yellow-400">
                  {formatStrictWithApprox(
                    aggregateProgress.bestValue,
                    aggregateProgress.leadingTotalTrades,
                    parseApproxConfigFromToml(aggregateProgress.leadingTomlConfig),
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Aggregate progress bar with glow effect */}
        <div className="mt-4 relative">
          {/* Glow effect behind the progress bar - color matches status */}
          <div
            className={cn(
              "absolute inset-0 blur-md opacity-50 rounded-full bg-gradient-to-r",
              statusCounts.completed === parallelGroup.studies.length
                ? "from-green-500 to-emerald-500"
                : statusCounts.failed > 0
                  ? "from-red-500 to-red-400"
                  : "from-sky-500 to-cyan-500"
            )}
            style={{
              width: `${Math.max(aggregateProgress.percentage, 5)}%`,
              height: "8px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
          <div className="flex items-center gap-3">
            <div ref={progressBarRef} className="flex-1 relative">
              <Progress
                value={aggregateProgress.percentage}
                className="h-2 bg-dark-800"
                variant={
                  statusCounts.completed === parallelGroup.studies.length
                    ? "success"
                    : statusCounts.failed > 0
                      ? "danger"
                      : "brand"
                }
              />
              {/* Slider dot indicator - color matches status */}
              <div
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-lg border-2 transition-all",
                  statusCounts.completed === parallelGroup.studies.length
                    ? "border-green-400"
                    : statusCounts.failed > 0
                      ? "border-red-400"
                      : "border-sky-400"
                )}
                style={{
                  left: `calc(${Math.min(aggregateProgress.percentage, 100)}% - 6px)`,
                }}
              />
            </div>
            {/* Percentage display */}
            <span className="text-sm text-muted-foreground font-medium tabular-nums w-12 text-right">
              {Math.round(aggregateProgress.percentage)}%
            </span>
          </div>
        </div>
      </GlassCardHeader>

      {/* Unified Tree Connector SVG - handles vertical rail AND branches as one element */}
      {/* CRITICAL: Dynamically positioned to align with progress bar's left edge */}
      {/* Uses measured progressBarPosition to eliminate hardcoded magic numbers */}
      {/* This ensures pixel-perfect alignment regardless of header content changes */}
      {isExpanded && showExpandedContent && progressBarPosition && (
        <svg
          ref={svgContainerRef}
          className="absolute pointer-events-none z-40"
          style={{
            left: `${progressBarPosition.left}px`,
            top: `${progressBarPosition.top}px`,
            width: '42px', // Branches reach card edge at x=34, plus margin
            height: `calc(100% - ${progressBarPosition.top}px - 16px)`,
            overflow: 'visible', // Allow orb glows to extend beyond SVG bounds
          }}
          data-testid="parallel-rail-connector"
        >
          {(() => {
            // Use TREE_CONNECTOR constants from bezierPath.ts to avoid magic numbers
            // These values are shared with AnimatedOrb for consistent path alignment
            const { railX, curveRadius, branchEndX } = TREE_CONNECTOR;

            // Build ordered list of measured center Y positions (matches card order)
            // Uses dynamic measurements from MeasuredCardWrapper for variable card heights
            const cardCenters: number[] = [];
            for (const study of sortedStudies) {
              const centerY = measuredPositions.get(study.study_id);
              if (centerY !== undefined) {
                cardCenters.push(centerY);
              }
            }

            // Don't render connectors until we have measurements
            if (cardCenters.length === 0) {
              return null;
            }

            // Rail ends where the last curve STARTS (not at card center) - no trailing "tail"
            // The curve starts at (centerY - curveRadius), so rail ends there
            const lastCardCenter = cardCenters[cardCenters.length - 1] ?? 0;
            const railEndY = lastCardCenter - curveRadius;

            // Use dark-700 (#334155) for subtle, modern look that blends with dark UI
            // Much more muted than dark-400, harmonizes with card borders and backgrounds
            const connectorColor = '#334155';

            return (
              // Group with opacity applied ONCE to the entire tree
              // This prevents opacity compounding at junctions where paths overlap
              <g opacity="0.6">
                {/* Main vertical rail - ends at last curve start (no tail) */}
                <path
                  d={`M ${railX} 0 L ${railX} ${railEndY}`}
                  fill="none"
                  stroke={connectorColor}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />

                {/* Branches - curved connectors using measured positions */}
                {/* Start at railX (same as rail) for perfect visual connection */}
                {cardCenters.map((centerY, index) => (
                  <path
                    key={index}
                    d={`M ${railX} ${centerY - curveRadius}
                        Q ${railX} ${centerY}, ${railX + curveRadius} ${centerY}
                        L ${branchEndX} ${centerY}`}
                    fill="none"
                    stroke={connectorColor}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                ))}
              </g>
            );
          })()}

          {/* Animated orbs for trial completions */}
          {/* Use measuredPositions for orb path alignment with SVG connectors */}
          <OrbManager
            studyPositions={measuredPositions}
            trialCounts={trialCounts}
            maxOrbs={8}
            color="#60a5fa"
            goldenPending={goldenPending}
            onGoldenConsumed={handleGoldenConsumed}
          />
        </svg>
      )}

      {/* Expanded child studies */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2 }}
            onAnimationComplete={() => {
              if (isExpanded) {
                setShowExpandedContent(true);
              }
            }}
            className="overflow-hidden bg-card/60"
            data-testid="parallel-expanded-shell"
          >
            {showExpandedContent && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                <GlassCardContent className="pt-1 pb-4 px-4">
                  {/* Child studies container - tree connector SVG is now unified above */}
                  <div className="relative ml-6">
                    <div className="space-y-3">
                      {/* Studies sorted by batch_index (V1, V2, V3...) for consistent order */}
                      {sortedStudies.map((study) => (
                        <MeasuredCardWrapper
                          key={study.study_id}
                          studyId={study.study_id}
                          onMeasure={handleCardMeasure}
                          svgContainerRef={svgContainerRef}
                          layoutKey={studyStatusKey}
                        >
                          <div className="relative">
                            {/* Modern card outline with left accent bar connecting to progress */}
                            <div
                              className="ml-6 rounded-xl border border-border/70 bg-card/50 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] relative overflow-hidden"
                              data-testid="parallel-child-card"
                            >
                              <LiveStudyCardWrapper
                                study={study}
                                latestEvent={liveEvents[study.study_id] ?? null}
                                onStop={() => onStop(study.study_id)}
                                onPause={() => onPause(study.study_id)}
                                onResume={() => onResume(study.study_id)}
                                onStart={() =>
                                  onStart(study.study_id, study.study_name)
                                }
                                onDelete={() =>
                                  onDelete(study.study_id, study.study_name)
                                }
                                isSelected={selectedStudies.has(study.study_id)}
                                isManageMode={isManageMode}
                                onToggleSelect={() =>
                                  onToggleSelect(study.study_id)
                                }
                                onNavigate={onNavigate}
                                isResuming={resumingStudyId === study.study_id}
                                isStarting={startingStudyId === study.study_id}
                                isLeader={displayedLeaderId === study.study_id}
                                allFrozen={allFrozen}
                                disableIntroAnimation
                                embedded
                              />
                            </div>
                          </div>
                        </MeasuredCardWrapper>
                      ))}
                    </div>
                  </div>
                </GlassCardContent>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </GlassCard>
  );
};

export function ActiveStudiesEnhanced() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { aiewToken, isLoading: loading } = useAuth();
  const api = useApi();
  const message = location.state?.message;
  const [currentPage, setCurrentPage] = useState(0);
  const [liveEvents, setLiveEvents] = useState<Record<string, SSEEvent>>({});

  useEffect(() => {
    if (!aiewToken) {
      setLiveEvents({});
    }
  }, [aiewToken]);

  const handleSSEEvent = useCallback((event: SSEEvent) => {
    if (!event.study_id) return;

    if (event.event !== "trial.completed" && event.event !== "study.status") {
      return;
    }

    const studyId = event.study_id;
    setLiveEvents((prev) => {
      const previousEvent = prev[studyId];
      if (previousEvent?.id === event.id) {
        return prev;
      }

      return {
        ...prev,
        [studyId]: event,
      };
    });
  }, []);

  // Single SSE connection for the entire page
  const { connectionState } = useModernSSE({
    enabled: !!aiewToken,
    ...(aiewToken && { token: aiewToken }),
    // Use polling as a robust fallback for all browsers (hybrid mode)
    // This ensures updates even if SSE connection is blocked or stalled
    pollInterval: 5000,
    onEvent: handleSSEEvent,
  });

  // Log connection state changes for debugging
  useEffect(() => {
    logger.info(`🔌 SSE Connection State changed to: ${connectionState}`);
  }, [connectionState]);

  // Only fetch data when we have an authenticated session
  const isAuthenticated = !!aiewToken && !loading;

  // Send auth state to Sentry for monitoring instead of console spam
  logger.info("ActiveStudiesEnhanced auth state", {
    hasSession: !!aiewToken,
    loading,
    isAuthenticated,
  });

  // Auto-refresh is always enabled
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedStudies, setSelectedStudies] = useState<Set<string>>(
    new Set()
  );
  const [isManageMode, setIsManageMode] = useState(false);
  // Track which parallel groups are expanded - lifted from child components
  // so expansion state persists across filter changes
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Sort configuration: field (updated/created) and direction (asc/desc)
  const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
    const stored = localStorage.getItem("active-studies-sort-config-v4");
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as SortConfig;
        if (
          (parsed.field === "updated" || parsed.field === "created") &&
          (parsed.direction === "asc" || parsed.direction === "desc")
        ) {
          return parsed;
        }
      } catch {
        // Invalid JSON, use default
      }
    }
    // Default: newest updated first
    return { field: "updated", direction: "desc" };
  });

  // Persist sort preference to localStorage
  useEffect(() => {
    localStorage.setItem("active-studies-sort-config-v4", JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Search state - debounced for server-side API calls
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    action: () => void;
    variant?: "default" | "destructive";
  }>({ open: false, title: "", description: "", action: () => {} });
  const messageToastShown = useRef(false);

  // Status filtering (including "parallel") is handled server-side.
  // When selectedStatus is "all", we omit the status parameter.
  const backendStatusFilter = getBackendStatusFilter(selectedStatus);
  const backendSearch = debouncedSearch || undefined;

  // Use modern SSE for real-time updates, with initial data load only
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["studies", currentPage, backendStatusFilter, backendSearch],
    queryFn: async () => {
      return await api.listStudies(
        currentPage * GROUPS_PER_PAGE,
        GROUPS_PER_PAGE,
        backendStatusFilter,
        backendSearch
      );
    },
    enabled: isAuthenticated, // CRITICAL: Only run the query when the user is authenticated
    // SSE provides real-time updates, no polling needed
    refetchInterval: false,
    // Only refetch on window focus for immediate updates
    refetchOnWindowFocus: true,
    // Cache data for 5 minutes since SSE keeps it fresh
    staleTime: 5 * 60 * 1000,
  });

  // Auto-correct best_value for completed parallel groups (AIE-289)

  // Lightweight stats query - uses the global cache to avoid observer-option drift
  const { data: statsData, isLoading: isLoadingOverall } = useGlobalStudiesCache();

  const overallStatusCounts = statsData?.status_counts ?? {};
  const overallTotalStudies = statsData?.total ?? 0;
  const overallActiveStudiesCount = (overallStatusCounts.running || 0) + (overallStatusCounts.queued || 0);
  const parallelCount = statsData?.parallel_count ?? 0;

  // Reset pagination when status filter or search changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedStatus, debouncedSearch]);

  // NOTE: Deprecated Supabase Realtime notifications removed.
  // Realtime updates now flow exclusively via SSE (useModernSSE),
  // which already invalidates relevant queries on events.

  // Stable callbacks to prevent infinite re-renders
  const handleClearFilters = useCallback(() => {
    setSelectedStatus("all");
  }, []);

  // Delete study mutation with optimistic cache update
  const deleteStudyMutation = useMutation({
    mutationFn: (studyId: string) => api.deleteStudy(studyId),
    onMutate: async (studyId) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ["studies"] });

      // Optimistically update ALL cached studies queries to mark this study as deleted
      // This ensures useActiveStudyCount immediately sees the study as non-running
      const allQueries = queryClient.getQueriesData<{
        studies?: Study[];
        total?: number;
      }>({ queryKey: ["studies"] });

      for (const [queryKey, data] of allQueries) {
        if (data?.studies && Array.isArray(data.studies)) {
          const updatedStudies = data.studies.map((study) =>
            study.study_id === studyId
              ? { ...study, status: "deleted" as const }
              : study
          );
          queryClient.setQueryData(queryKey, {
            ...data,
            studies: updatedStudies,
          });
        }
      }

      // Also invalidate the active-count query to force recalculation
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });

      return { studyId };
    },
    onSuccess: (_, studyId) => {
      // Force immediate refresh
      refetch();
      queryClient.invalidateQueries({ queryKey: ["studies"] });
      queryClient.removeQueries({ queryKey: ["study", studyId] });
      // Force active count to recalculate
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      // Refresh credit display (credits may be released)
      queryClient.invalidateQueries({ queryKey: ["userLimits"] });
      addToast({
        title: "Study deleted",
        description: "The study has been removed successfully.",
        variant: "success",
      });
      setSelectedStudies((prev) => {
        const next = new Set(prev);
        next.delete(studyId);
        return next;
      });
    },
    onError: (error, _studyId) => {
      // On error, invalidate to refetch correct state
      queryClient.invalidateQueries({ queryKey: ["studies"] });
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      addToast({
        title: "Failed to delete study",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "error",
      });
    },
  });

  // Bulk delete mutation with optimistic cache update
  const bulkDeleteMutation = useMutation({
    mutationFn: async (studyIds: string[]) => {
      const results = await Promise.allSettled(
        studyIds.map((id) => api.deleteStudy(id))
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        throw new Error(`Failed to delete ${failed} studies`);
      }
      return results;
    },
    onMutate: async (studyIds) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ["studies"] });

      const studyIdSet = new Set(studyIds);

      // Optimistically update ALL cached studies queries to mark these studies as deleted
      const allQueries = queryClient.getQueriesData<{
        studies?: Study[];
        total?: number;
      }>({ queryKey: ["studies"] });

      for (const [queryKey, data] of allQueries) {
        if (data?.studies && Array.isArray(data.studies)) {
          const updatedStudies = data.studies.map((study) =>
            studyIdSet.has(study.study_id)
              ? { ...study, status: "deleted" as const }
              : study
          );
          queryClient.setQueryData(queryKey, {
            ...data,
            studies: updatedStudies,
          });
        }
      }

      // Force active count to recalculate immediately
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });

      return { studyIds };
    },
    onSuccess: (_, studyIds) => {
      // Force immediate refresh
      refetch();
      queryClient.invalidateQueries({ queryKey: ["studies"] });
      studyIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: ["study", id] });
      });
      // Force active count to recalculate
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      // Refresh credit display (credits may be released)
      queryClient.invalidateQueries({ queryKey: ["userLimits"] });
      addToast({
        title: "Studies deleted",
        description: `${studyIds.length} studies have been removed successfully.`,
        variant: "success",
      });
      setSelectedStudies(new Set());
    },
    onError: (error) => {
      // On error, invalidate to refetch correct state
      queryClient.invalidateQueries({ queryKey: ["studies"] });
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      addToast({
        title: "Failed to delete studies",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "error",
      });
    },
  });

  // Bulk pause mutation with optimistic cache update
  const bulkPauseMutation = useMutation({
    mutationFn: async (studyIds: string[]) => {
      return api.bulkPauseStudies(studyIds);
    },
    onMutate: async (studyIds) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ["studies"] });

      const studyIdSet = new Set(studyIds);

      // Optimistically update ALL cached studies queries to mark these studies as paused
      const allQueries = queryClient.getQueriesData<{
        studies?: Study[];
        total?: number;
      }>({ queryKey: ["studies"] });

      for (const [queryKey, data] of allQueries) {
        if (data?.studies && Array.isArray(data.studies)) {
          const updatedStudies = data.studies.map((study) =>
            studyIdSet.has(study.study_id)
              ? { ...study, status: "paused" as const }
              : study
          );
          queryClient.setQueryData(queryKey, {
            ...data,
            studies: updatedStudies,
          });
        }
      }

      // Force active count to recalculate immediately
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });

      return { studyIds };
    },
    onSuccess: (response) => {
      // Force immediate refresh
      refetch();
      queryClient.invalidateQueries({ queryKey: ["studies"] });
      // Force active count to recalculate
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      addToast({
        title: "Studies paused",
        description: `${response.successful_count} studies have been paused successfully.`,
        variant: "success",
      });
      setSelectedStudies(new Set());
    },
    onError: (error) => {
      // On error, invalidate to refetch correct state
      queryClient.invalidateQueries({ queryKey: ["studies"] });
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      addToast({
        title: "Failed to pause studies",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "error",
      });
    },
  });

  // Stop study mutation with optimistic update for instant UI feedback
  const stopStudyMutation = useMutation({
    mutationFn: (studyId: string) => api.stopStudy(studyId),
    onMutate: async (studyId) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ["studies"] });

      // Snapshot all studies queries for rollback
      const allQueries = queryClient.getQueriesData<{
        studies?: Study[];
        total?: number;
      }>({ queryKey: ["studies"] });

      // Optimistically update ALL cached studies queries - stopped studies go to "completed"
      for (const [queryKey, data] of allQueries) {
        if (data?.studies && Array.isArray(data.studies)) {
          const updatedStudies = data.studies.map((study) =>
            study.study_id === studyId
              ? { ...study, status: "completed" as const }
              : study
          );
          queryClient.setQueryData(queryKey, {
            ...data,
            studies: updatedStudies,
          });
        }
      }

      // Also invalidate active-count immediately for instant counter update
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });

      // Return snapshot for rollback
      return { previousQueries: allQueries };
    },
    onSuccess: (_, studyId) => {
      // Invalidate to ensure consistency with server state
      queryClient.invalidateQueries({ queryKey: ["study", studyId] });
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      // Refresh credit display (partial credits released on stop)
      queryClient.invalidateQueries({ queryKey: ["userLimits"] });
      addToast({
        title: "Study stopped",
        description: "The study has been stopped successfully.",
        variant: "success",
      });
    },
    onError: (error, _studyId, context) => {
      // Rollback to previous state on error
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      addToast({
        title: "Failed to stop study",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "error",
      });
    },
  });

  // Pause study mutation with optimistic update for instant UI feedback
  const pauseStudyMutation = useMutation({
    mutationFn: (studyId: string) => api.pauseStudy(studyId),
    onMutate: async (studyId) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ["studies"] });

      // Snapshot all studies queries for rollback
      const allQueries = queryClient.getQueriesData<{
        studies?: Study[];
        total?: number;
      }>({ queryKey: ["studies"] });

      // Optimistically update ALL cached studies queries
      for (const [queryKey, data] of allQueries) {
        if (data?.studies && Array.isArray(data.studies)) {
          const updatedStudies = data.studies.map((study) =>
            study.study_id === studyId
              ? { ...study, status: "paused" as const }
              : study
          );
          queryClient.setQueryData(queryKey, {
            ...data,
            studies: updatedStudies,
          });
        }
      }

      // Return snapshot for rollback
      return { previousQueries: allQueries };
    },
    onSuccess: (_, studyId) => {
      // Invalidate to ensure consistency with server state
      queryClient.invalidateQueries({ queryKey: ["study", studyId] });
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      addToast({
        title: "Study Paused",
        description: "The study has been paused. You can resume it anytime.",
        variant: "success",
      });
    },
    onError: (error, _studyId, context) => {
      // Rollback to previous state on error
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      addToast({
        title: "Failed to pause study",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "error",
      });
    },
  });

  // Resume study mutation with optimistic update for instant UI feedback
  const resumeStudyMutation = useMutation({
    mutationFn: (studyId: string) => api.resumeStudy(studyId),
    onMutate: async (studyId) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ["studies"] });

      // Snapshot all studies queries for rollback
      const allQueries = queryClient.getQueriesData<{
        studies?: Study[];
        total?: number;
      }>({ queryKey: ["studies"] });

      // Find the study to determine previous status (for toast message)
      let wasQueued = false;
      for (const [, data] of allQueries) {
        const study = data?.studies?.find((s) => s.study_id === studyId);
        if (study) {
          wasQueued = study.status === "queued";
          break;
        }
      }

      // Optimistically update ALL cached studies queries - resumed studies go to "queued"
      for (const [queryKey, data] of allQueries) {
        if (data?.studies && Array.isArray(data.studies)) {
          const updatedStudies = data.studies.map((study) =>
            study.study_id === studyId
              ? {
                  ...study,
                  status: "queued" as const,
                  trials_per_second: undefined,
                  optimization_duration_seconds: undefined,
                }
              : study
          );
          queryClient.setQueryData(queryKey, {
            ...data,
            studies: updatedStudies,
          });
        }
      }

      // Return snapshot for rollback and metadata for toast
      return { previousQueries: allQueries, wasQueued };
    },
    onSuccess: (_, studyId, context) => {
      // Invalidate to ensure consistency with server state
      queryClient.invalidateQueries({ queryKey: ["study", studyId] });
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      const wasQueued = context?.wasQueued ?? false;
      addToast({
        title: wasQueued ? "Study Re-queued" : "Study Resumed",
        description: wasQueued
          ? "The study has been re-queued for processing."
          : "The study has been queued to resume from where it left off.",
        variant: "success",
      });
    },
    onError: (error, _studyId, context) => {
      // Rollback to previous state on error
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      addToast({
        title: "Failed to resume study",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "error",
      });
    },
  });

  // Start queued study mutation with optimistic update for instant UI feedback
  const startQueuedStudyMutation = useMutation({
    mutationFn: (studyId: string) => api.startQueuedStudy(studyId),
    onMutate: async (studyId) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ["studies"] });

      // Snapshot all studies queries for rollback
      const allQueries = queryClient.getQueriesData<{
        studies?: Study[];
        total?: number;
      }>({ queryKey: ["studies"] });

      // Optimistically update ALL cached studies queries - started studies go to "queued" (with job_id)
      for (const [queryKey, data] of allQueries) {
        if (data?.studies && Array.isArray(data.studies)) {
          const updatedStudies = data.studies.map((study) =>
            study.study_id === studyId
              ? { ...study, status: "queued" as const, job_id: "pending" }
              : study
          );
          queryClient.setQueryData(queryKey, {
            ...data,
            studies: updatedStudies,
          });
        }
      }

      // Return snapshot for rollback
      return { previousQueries: allQueries };
    },
    onSuccess: (_, studyId) => {
      // Invalidate to ensure consistency with server state
      queryClient.invalidateQueries({ queryKey: ["study", studyId] });
      queryClient.invalidateQueries({ queryKey: ["studies", "active-count"] });
      addToast({
        title: "Study Started",
        description: "The study has been started and is now processing.",
        variant: "success",
      });
    },
    onError: (error, _studyId, context) => {
      // Rollback to previous state on error
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      addToast({
        title: "Failed to start study",
        description:
          error instanceof Error ? error.message : "An error occurred",
        variant: "error",
      });
    },
  });

  // Studies are already filtered in the backend to exclude deleted/cancelled
  // Wrap in useMemo to prevent new array reference on every render
  const studies = useMemo(() => data?.studies || [], [data?.studies]);
  // Use the 'total' from the API response for accurate pagination
  const totalStudies = data?.total || 0;
  const totalPages = Math.ceil(totalStudies / GROUPS_PER_PAGE);

  // Debug logging for pagination
  useEffect(() => {
    if (data) {
      logger.info("📊 Pagination debug:", {
        totalFromAPI: data.total,
        studiesOnPage: data.studies?.length,
        currentPage,
        totalPages,
        expectedTotal: totalStudies,
      });

      // Warn if total equals page size (indicates potential bug)
      if (
        data.total === GROUPS_PER_PAGE &&
        data.studies?.length === GROUPS_PER_PAGE
      ) {
        logger.warn(
          "⚠️ Total count equals page size - possible pagination bug!"
        );
      }
    }
  }, [data, currentPage, totalPages, totalStudies]);

  // Clear message after showing, but only show the toast once
  useEffect(() => {
    if (message && !messageToastShown.current) {
      addToast({
        title: "Success",
        description: message,
        variant: "success",
      });
      // Mark the toast as shown for this render cycle
      messageToastShown.current = true;
      // Clear the state from browser history to prevent re-show on refresh
      window.history.replaceState({}, document.title);
    }
  }, [message, addToast]);

  // Keyboard shortcut for manage mode (Cmd+M or Ctrl+M)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "m") {
        e.preventDefault();
        setIsManageMode((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Clear selections when exiting manage mode
  useEffect(() => {
    if (!isManageMode) {
      setSelectedStudies(new Set());
    }
  }, [isManageMode]);

  // Sort studies for display. Search and status filtering are handled server-side;
  // only client-side sorting is applied here for instant UI updates.
  const filteredStudies = useMemo(() => {
    return filterAndSortStudies({ studies, sortConfig });
  }, [studies, sortConfig]);

  if (loading) {
    return (
      <CandlestickLoader
        isLoading
        context="ActiveStudiesEnhanced:auth"
        containerClassName="w-full min-h-[70vh] px-6"
        messageClassName="text-slate-200"
        messages={ACTIVE_STUDIES_AUTH_MESSAGES}
      />
    );
  }

  if (!aiewToken) {
    return null;
  }

  // Note: activeStudies and statusCounts for filtered data removed.
  // Quick Stats and filter tabs now use overall* versions (unfiltered) for accurate totals.

  const handleDeleteStudy = (studyId: string, studyName?: string) => {
    setConfirmDialog({
      open: true,
      title: "Delete Study",
      description: `Are you sure you want to delete ${studyName || "this study"}? This action cannot be undone.`,
      variant: "destructive",
      action: () => deleteStudyMutation.mutate(studyId),
    });
  };

  const handleBulkDelete = () => {
    const selectedArray = Array.from(selectedStudies);
    setConfirmDialog({
      open: true,
      title: "Delete Selected Studies",
      description: `Are you sure you want to delete ${selectedArray.length} selected studies? This action cannot be undone.`,
      variant: "destructive",
      action: () => bulkDeleteMutation.mutate(selectedArray),
    });
  };

  const handleBulkPause = () => {
    const selectedArray = Array.from(selectedStudies);
    // Filter to only running studies that can actually be paused
    const pausableStudies = selectedArray.filter((studyId) => {
      const study = studies.find((s) => s.study_id === studyId);
      return study?.status === "running";
    });
    if (pausableStudies.length === 0) {
      addToast({
        title: "No pausable studies",
        description: "None of the selected studies are currently running.",
        variant: "info",
      });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Pause Selected Studies",
      description: `Are you sure you want to pause ${pausableStudies.length} running studies? They will be paused and can be resumed later.`,
      variant: "default",
      action: () => bulkPauseMutation.mutate(pausableStudies),
    });
  };

  const handleStopStudy = (studyId: string, studyName?: string) => {
    setConfirmDialog({
      open: true,
      title: "Stop Study",
      description: `Are you sure you want to stop ${studyName || "this study"}? The study will be cancelled and marked as stopped. You can still view the results collected so far.`,
      variant: "default",
      action: () => stopStudyMutation.mutate(studyId),
    });
  };

  const handlePauseStudy = (studyId: string) => {
    // No confirmation needed for pause, but let's be safe
    pauseStudyMutation.mutate(studyId);
  };

  const handleResumeStudy = (studyId: string) => {
    resumeStudyMutation.mutate(studyId);
  };

  const handleStartQueuedStudy = (studyId: string, studyName?: string) => {
    setConfirmDialog({
      open: true,
      title: "Start Study",
      description: `Are you sure you want to start ${studyName || "this study"} now? It will begin processing immediately if a slot is available.`,
      variant: "default",
      action: () => startQueuedStudyMutation.mutate(studyId),
    });
  };

  const handleClearFailed = () => {
    const failedStudies = studies
      .filter((s) => s.status === "failed")
      .map((s) => s.study_id);
    if (failedStudies.length === 0) return;

    setConfirmDialog({
      open: true,
      title: "Clear Failed Studies",
      description: `Are you sure you want to delete all ${failedStudies.length} failed studies?`,
      variant: "destructive",
      action: () => bulkDeleteMutation.mutate(failedStudies),
    });
  };

  const toggleSelectAll = () => {
    if (selectedStudies.size === filteredStudies.length) {
      setSelectedStudies(new Set());
    } else {
      setSelectedStudies(new Set(filteredStudies.map((s) => s.study_id)));
    }
  };

  const toggleSelectStudy = (studyId: string) => {
    setSelectedStudies((prev) => {
      const next = new Set(prev);
      if (next.has(studyId)) {
        next.delete(studyId);
      } else {
        next.add(studyId);
      }
      return next;
    });
  };

  // Toggle expansion state for a parallel group
  // Lifted to parent so state persists across filter changes
  const toggleGroupExpansion = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "queued":
        return <Clock className="h-4 w-4 text-amber-400" />;
      case "running":
        return <Activity className="h-4 w-4 text-blue-400" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "failed":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case "paused":
        return <Pause className="h-4 w-4 text-slate-400" />;
      case "cancelled":
      case "deleted":
        return <X className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Study Dashboard"
        description="Monitor live progress and manage the execution of your studies."
        badge={
          <div
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
              connectionState === "connected"
                ? "bg-green-500/10 border-green-500/20 text-green-500"
                : connectionState === "connecting" ||
                    connectionState === "reconnecting"
                  ? "bg-yellow-500/10 border-yellow-500/20 text-yellow-500"
                  : "bg-red-500/10 border-red-500/20 text-red-500"
            )}
          >
            {connectionState === "connected" ? (
              <>
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span>Live</span>
              </>
            ) : (
              <>
                <WifiOff className="h-3 w-3" />
                <span className="capitalize">{connectionState}</span>
              </>
            )}
          </div>
        }
        actions={
          <>
            {isManageMode && selectedStudies.size > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkPause}
                  disabled={bulkPauseMutation.isPending}
                  className="bg-slate-500/10 border-slate-500/30 text-slate-300 hover:bg-slate-500/20"
                >
                  {bulkPauseMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Pause className="h-4 w-4 mr-2" />
                  )}
                  Pause Running
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteMutation.isPending}
                >
                  {bulkDeleteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete {selectedStudies.size} Selected
                </Button>
              </>
            )}

            {(overallStatusCounts.failed || 0) > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearFailed}
                className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Failed
              </Button>
            )}
          </>
        }
      />

      <SystemPausedBanner />

      {/* Quick Stats - Moved up for better visibility */}
      {/* Uses overall stats (unfiltered) so counts are accurate regardless of current filter */}
      {(studies.length > 0 || overallTotalStudies > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 -mt-2">
          <GlassCard>
            <GlassCardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Live Studies</p>
                  <p className="text-2xl font-bold">{overallActiveStudiesCount}</p>
                </div>
                <Activity className="h-8 w-8 text-brand-blue" />
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Paused Studies</p>
                  <p className="text-2xl font-bold">{overallStatusCounts.paused || 0}</p>
                </div>
                <Pause className="h-8 w-8 text-slate-500" />
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Failed Studies</p>
                  <p className="text-2xl font-bold">{overallStatusCounts.failed || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed Studies</p>
                  <p className="text-2xl font-bold">{overallStatusCounts.completed || 0}</p>
                </div>
                <Trophy className="h-8 w-8 text-green-500" />
              </div>
            </GlassCardContent>
          </GlassCard>
        </div>
      )}

      {/* Manage Mode Help Text */}
      {isManageMode && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-muted-foreground bg-dark-800/50 px-4 py-2 rounded-lg"
        >
          Select studies to delete them. Press{" "}
          <kbd className="px-2 py-0.5 bg-dark-700 rounded text-xs">⌘M</kbd> to
          exit manage mode.
        </motion.div>
      )}

      {/* Search Bar */}
      <SearchFilterToolbar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by name, ID, or symbol..."
        inputId="studies-search"
        hasActiveFilters={searchQuery !== "" || selectedStatus !== "all"}
        onClearFilters={() => {
          setSearchQuery("");
          setSelectedStatus("all");
        }}
      />

      {/* Screen reader announcement for search results - only announce after server responds */}
      <div role="status" aria-live="polite" className="sr-only">
        {debouncedSearch && searchQuery.trim() === debouncedSearch && `${totalStudies} ${totalStudies === 1 ? 'study' : 'studies'} found`}
      </div>

      {/* Status Filter Tabs */}
      {/* Uses overall stats (unfiltered) so tab counts reflect true totals */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 text-sm pl-1">
        <div className="flex gap-2 flex-1">
          {(
            [
              { key: "all", label: "All", count: overallTotalStudies },
              // Add Parallel Filter
              {
                key: "parallel",
                label: "Parallel",
                count: parallelCount,
              },
              ...Object.entries(overallStatusCounts).map(([status, count]) => ({
                key: status,
                label: status,
                count,
              })),
            ] as Array<{ key: string; label: string; count: number }>
          ).map((filter) => {
            const isSelected = selectedStatus === filter.key;
            return (
              <button
                key={filter.key}
                onClick={() => setSelectedStatus(filter.key)}
                className={cn(
                  "relative flex items-center gap-2 rounded-full px-4 py-1.5 transition-all overflow-hidden",
                  isSelected
                    ? "bg-secondary text-secondary-foreground ring-1 ring-brand-blue/50"
                    : "bg-secondary/30 text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                )}
              >
                <span className="flex items-center gap-2 capitalize">
                  {filter.key !== "all" &&
                    filter.key !== "parallel" &&
                    getStatusIcon(filter.key)}
                  {filter.key === "parallel" && (
                    <Layers className="h-4 w-4 text-blue-400" />
                  )}
                  {filter.label}
                </span>
                <span
                  className={cn(
                    "text-xs",
                    isSelected ? "opacity-80" : "opacity-60"
                  )}
                >
                  {filter.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Manage Button - next to sort controls */}
        <Button
          variant={isManageMode ? "default" : "outline"}
          size="sm"
          onClick={() => setIsManageMode(!isManageMode)}
          className={cn(
            "gap-2",
            isManageMode
              ? "bg-gradient-to-r from-brand-blue to-brand-blue-dark"
              : "bg-secondary/50 hover:bg-secondary"
          )}
        >
          <Settings2 className="h-4 w-4" />
          {isManageMode ? "Done" : "Manage"}
        </Button>

        {/* Sort Control - Field selector with direction toggle */}
        <SortControl
          id="active-studies"
          value={sortConfig}
          onChange={setSortConfig}
        />
      </div>

      {/* Select All Checkbox - Only show in manage mode */}
      {isManageMode && filteredStudies.length > 0 && (
        <div className="flex items-center gap-2 px-2">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {selectedStudies.size === filteredStudies.length ? (
              <CheckSquare className="h-4 w-4" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            Select All
          </button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="w-full">
          <ErrorStateHandler
            error={error as Error}
            onRetry={refetch}
            onLogin={() => {
              // Redirect to Google OAuth
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
              window.location.href = `${supabaseUrl}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(window.location.origin)}`;
            }}
          />
        </div>
      )}

      {/* Studies List */}
      {!error && (isLoading || isLoadingOverall) ? (
        <CandlestickLoader
          isLoading={isLoading || isLoadingOverall}
          context="ActiveStudiesEnhanced:data"
          containerClassName="w-full min-h-[70vh] px-6"
          messageClassName="text-slate-200"
          messages={ACTIVE_STUDIES_DATA_MESSAGES}
        />
      ) : !error && filteredStudies.length === 0 ? (
        <div className="w-full">
          {overallTotalStudies === 0 ? (
            <NoStudiesEmptyState onCreateStudy={() => navigate("/new-study")} />
          ) : (
            <NoFilteredStudiesEmptyState onClearFilters={handleClearFilters} />
          )}
        </div>
      ) : !error ? (
        <AnimatePresence mode="popLayout">
          <motion.div className="space-y-4" data-testid="studies-list">
            {/* Render parallel study groups and standalone studies in unified sorted order */}
            {(() => {
              const items: React.ReactNode[] = [];

              const displayItems = buildStudyDisplayItems(
                filteredStudies,
                sortConfig
              );

              // Render items in unified sorted order
              displayItems.forEach((item, animationIndex) => {
                if (item.type === "parallel") {
                  const parallelGroup = item.group;
                  const key = `parallel-${parallelGroup.parallel_group_id}`;
                  // If we are showing "All" or "Parallel", allow the group component to render
                  // The filtering logic inside filteredStudies handles the data limitation
                  items.push(
                    <motion.div
                      key={key}
                      data-testid="parallel-study-card"
                      data-parallel-group-id={parallelGroup.parallel_group_id}
                      layout="position"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: animationIndex * 0.05 }}
                    >
                      <LiveStudyBundleCard
                        parallelGroup={parallelGroup}
                        liveEvents={liveEvents}
                        onStop={handleStopStudy}
                        onPause={handlePauseStudy}
                        onResume={handleResumeStudy}
                        onStart={handleStartQueuedStudy}
                        onDelete={handleDeleteStudy}
                        isManageMode={isManageMode}
                        selectedStudies={selectedStudies}
                        onToggleSelect={toggleSelectStudy}
                        onNavigate={navigate}
                        resumingStudyId={
                          resumeStudyMutation.isPending
                            ? (resumeStudyMutation.variables as string)
                            : null
                        }
                        startingStudyId={
                          startQueuedStudyMutation.isPending
                            ? (startQueuedStudyMutation.variables as string)
                            : null
                        }
                        allFrozen={statsData?.all_frozen === true}
                        isExpanded={expandedGroups.has(parallelGroup.parallel_group_id)}
                        onToggleExpand={() => toggleGroupExpansion(parallelGroup.parallel_group_id)}
                      />
                    </motion.div>
                  );
                } else {
                  const study = item.study;
                  items.push(
                    <motion.div
                      key={study.study_id}
                      data-testid="study-card"
                      data-study-id={study.study_id}
                      layout="position"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: animationIndex * 0.05 }}
                    >
                      <LiveStudyCardWrapper
                        study={study}
                        latestEvent={liveEvents[study.study_id] ?? null}
                        onStop={() => handleStopStudy(study.study_id)}
                        onPause={() => handlePauseStudy(study.study_id)}
                        onResume={() => handleResumeStudy(study.study_id)}
                        onStart={() =>
                          handleStartQueuedStudy(
                            study.study_id,
                            study.study_name
                          )
                        }
                        onDelete={() =>
                          handleDeleteStudy(study.study_id, study.study_name)
                        }
                        isSelected={selectedStudies.has(study.study_id)}
                        isManageMode={isManageMode}
                        onToggleSelect={() => toggleSelectStudy(study.study_id)}
                        onNavigate={navigate}
                        isResuming={
                          resumeStudyMutation.isPending &&
                          resumeStudyMutation.variables === study.study_id
                        }
                        isStarting={
                          startQueuedStudyMutation.isPending &&
                          startQueuedStudyMutation.variables === study.study_id
                        }
                        allFrozen={statsData?.all_frozen === true}
                      />
                    </motion.div>
                  );
                }
              });

              return items;
            })()}
          </motion.div>
        </AnimatePresence>
      ) : null}

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center items-center gap-4"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="bg-dark-800/50 border-dark-700"
          >
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              const pageNum = currentPage > 2 ? currentPage - 2 + i : i;
              if (pageNum >= totalPages) return null;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={cn(
                    "w-8 h-8 rounded-full text-sm font-medium transition-all",
                    currentPage === pageNum
                      ? "bg-gradient-to-r from-brand-blue to-brand-blue-dark text-white"
                      : "bg-dark-800/50 text-muted-foreground hover:bg-dark-700/50"
                  )}
                >
                  {pageNum + 1}
                </button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
            }
            disabled={currentPage >= totalPages - 1}
            className="bg-dark-800/50 border-dark-700"
          >
            Next
          </Button>
        </motion.div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant={
          (confirmDialog.variant ?? "default") as "default" | "destructive"
        }
        onConfirm={confirmDialog.action}
      />
    </div>
  );
}

interface StudyCardProps {
  study: Study;
  isSelected: boolean;
  onToggleSelect: () => void;
  onDelete: () => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onStart: () => void;
  isManageMode: boolean;
  onNavigate: (path: string) => void;

  isResuming: boolean;
  isStarting: boolean;
  disableIntroAnimation?: boolean;
  embedded?: boolean;
  isLeader?: boolean;
  allFrozen?: boolean;
  liveProgress?: {
    current_trial: number;
    total_trials: number;
    progress_percentage: number;
    estimated_remaining_ms?: number;
    best_value?: number;
    trials_per_second?: number;
  } | null;
}

function StudyCard({
  study,
  isSelected,
  onToggleSelect,
  onDelete,
  onStop,
  onPause,
  onResume,
  onStart,
  isManageMode,
  onNavigate,
  isResuming,
  isStarting,
  disableIntroAnimation = false,
  embedded = false,
  isLeader = false,
  allFrozen = false,
  liveProgress,
}: StudyCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const disableMotion = embedded || disableIntroAnimation;

  // AIE-539: label (tag) save + known category colors, mirroring the Library.
  const { save: saveLabel, knownLabelColors } = useSaveStudyLabel(study.study_id);

  // Duration display removed per design request

  // Cache last known progress/best so we don't momentarily regress to 0/N/A
  const lastKnownTrial = useRef<number | null>(null);
  const lastKnownTotal = useRef<number | null>(null);
  const lastKnownBest = useRef<number | null>(null);
  const lastKnownEstimatedRemainingMs = useRef<number | null>(null);
  const lastKnownThroughput = useRef<number | null>(null);

  // Clear cached progress when study is retried (status becomes "queued")
  useEffect(() => {
    if (study.status === "queued") {
      lastKnownTrial.current = null;
      lastKnownTotal.current = null;
      lastKnownBest.current = null;
      lastKnownEstimatedRemainingMs.current = null;
      lastKnownThroughput.current = null;
    }
  }, [study.status]);

  useEffect(() => {
    if (liveProgress?.current_trial != null) {
      lastKnownTrial.current = liveProgress.current_trial;
    }
    if (liveProgress?.total_trials != null) {
      lastKnownTotal.current = liveProgress.total_trials;
    }
    if (liveProgress?.best_value != null) {
      lastKnownBest.current = liveProgress.best_value;
    }
    if (liveProgress?.estimated_remaining_ms != null) {
      lastKnownEstimatedRemainingMs.current =
        liveProgress.estimated_remaining_ms;
    }
    if (liveProgress?.trials_per_second != null) {
      lastKnownThroughput.current = liveProgress.trials_per_second;
    }
  }, [
    liveProgress?.current_trial,
    liveProgress?.total_trials,
    liveProgress?.best_value,
    liveProgress?.estimated_remaining_ms,
    liveProgress?.trials_per_second,
  ]);

  useEffect(() => {
    if (
      study.n_trials_completed != null &&
      study.n_trials_completed > (lastKnownTrial.current ?? 0)
    ) {
      lastKnownTrial.current = study.n_trials_completed;
    }
    if (
      study.n_trials != null &&
      study.n_trials > (lastKnownTotal.current ?? 0)
    ) {
      lastKnownTotal.current = study.n_trials;
    }
    if (study.best_value != null) {
      lastKnownBest.current = study.best_value;
    }
  }, [study.n_trials_completed, study.n_trials, study.best_value]);

  // Resolve trials numbers with sensible fallbacks
  const resolvedTotalTrials =
    liveProgress?.total_trials ??
    study.n_trials ??
    lastKnownTotal.current ??
    study.total_trials ??
    100;

  let resolvedCurrentTrial =
    // For queued studies (freshly retried), prioritize database values over cached data
    study.status === "queued"
      ? (study.current_trial ?? study.n_trials_completed ?? 0)
      : (liveProgress?.current_trial ??
        study.current_trial ??
        study.n_trials_completed ??
        lastKnownTrial.current ??
        0);

  if (study.status === "completed") {
    // Ensure completed studies show full trial count even if DB update is slightly delayed
    if (resolvedCurrentTrial === 0 && resolvedTotalTrials > 0) {
      resolvedCurrentTrial = resolvedTotalTrials;
    } else if (resolvedCurrentTrial < resolvedTotalTrials) {
      resolvedCurrentTrial = resolvedTotalTrials;
    }
  }

  // DEFENSIVE: Clamp current trial to never exceed total trials
  // This handles edge cases where Optuna's cumulative trial.number exceeds
  // the study's configured n_trials (e.g., after resume/retry scenarios)
  if (resolvedCurrentTrial > resolvedTotalTrials) {
    resolvedCurrentTrial = resolvedTotalTrials;
  }

  const baseProgress =
    liveProgress?.progress_percentage ??
    (resolvedTotalTrials > 0
      ? (resolvedCurrentTrial / resolvedTotalTrials) * 100
      : 0);

  const progress =
    study.status === "completed"
      ? 100
      : Math.max(0, Math.min(100, baseProgress));

  const estimatedTimeRemaining =
    liveProgress?.estimated_remaining_ms ??
    lastKnownEstimatedRemainingMs.current ??
    undefined;
  const estimatedMinutesRemaining =
    estimatedTimeRemaining != null
      ? Math.max(0, Math.round(estimatedTimeRemaining / 1000 / 60))
      : null;
  const bestValue =
    liveProgress?.best_value ??
    study.best_value ??
    (lastKnownBest.current != null ? lastKnownBest.current : undefined);
  const trialsPerSecond =
    liveProgress?.trials_per_second ?? lastKnownThroughput.current ?? undefined;

  const handleStop = () => {
    onStop();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "queued":
        return "from-sky-500/20 to-sky-600/20 border-sky-500/30";
      case "running":
        return "from-blue-500/20 to-blue-600/20 border-blue-500/30";
      case "completed":
        return "from-green-500/20 to-emerald-600/20 border-green-500/30";
      case "failed":
        return "from-red-500/20 to-red-600/20 border-red-500/30";
      case "paused":
        return "from-slate-500/20 to-slate-600/20 border-slate-500/30";
      default:
        return "from-gray-500/20 to-gray-600/20 border-gray-500/30";
    }
  };

  return (
    <GlassCard
      initial={disableMotion ? false : undefined}
      animate={disableMotion ? false : undefined}
      className={cn(
        "overflow-hidden transition-all duration-300 cursor-pointer",
        !embedded && "hover:scale-[1.02]",
        !embedded && "hover:shadow-glow",
        study.status === "paused" && "grayscale-[0.4] opacity-90",
        isSelected && "ring-2 ring-brand-blue",
        embedded &&
          "bg-transparent border-transparent shadow-none hover:bg-transparent hover:border-transparent hover:shadow-none backdrop-blur-0"
      )}
      onClick={() => {
        if (isManageMode) {
          onToggleSelect();
        } else {
          onNavigate(`/results/${study.study_id}`);
        }
      }}
      data-testid="active-study-card"
    >
      {!embedded && (
        <div
          className={cn(
            "absolute inset-0 bg-gradient-to-r opacity-5",
            getStatusColor(study.status)
          )}
        />
      )}

      <GlassCardHeader className="relative pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isManageMode && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelect();
                }}
                className="p-1 rounded hover:bg-dark-800/50 transition-colors"
              >
                {isSelected ? (
                  <CheckSquare className="h-5 w-5 text-brand-blue" />
                ) : (
                  <Square className="h-5 w-5 text-muted-foreground" />
                )}
              </button>
            )}

            <div
              className={cn(
                "p-2 rounded-lg bg-gradient-to-r",
                getStatusColor(study.status)
              )}
              data-testid={`study-status-${study.status}`}
            >
              {(() => {
                switch (study.status) {
                  case "queued":
                    return <Clock className="h-5 w-5 text-amber-400" />;
                  case "running":
                    return <Activity className="h-5 w-5 text-blue-400" />;
                  case "completed":
                    return <CheckCircle className="h-5 w-5 text-green-400" />;
                  case "failed":
                    return <AlertCircle className="h-5 w-5 text-red-400" />;
                  case "paused":
                    return <Pause className="h-5 w-5 text-slate-500" />;
                  case "cancelled":
                  case "deleted":
                    return <X className="h-5 w-5 text-gray-400" />;
                  default:
                    return <AlertCircle className="h-5 w-5 text-gray-400" />;
                }
              })()}
            </div>

            {/* Leader trophy - shows which study has the highest best value in the group */}
            {isLeader && (
              <div className="p-1.5 rounded-lg bg-yellow-500/20 border border-yellow-500/40">
                <Trophy className="h-4 w-4 text-yellow-500" />
              </div>
            )}

            <div>
              <GlassCardTitle className="text-lg font-bold tracking-tight line-clamp-1">
                {study.study_name
                  .replace(/^sample_/, "")
                  .toUpperCase()
                  .replace(/_/g, " ")}
              </GlassCardTitle>
              <GlassCardDescription className="text-[10px] font-mono opacity-60 mt-0.5">
                {study.study_id.slice(0, 8)}
              </GlassCardDescription>
            </div>

            {/* AIE-539: label icon / colored chip + inline editor (mirrors the Library) */}
            <StudyLabelControl
              label={study.user_label}
              color={study.user_label_color}
              knownLabelColors={knownLabelColors}
              onSave={saveLabel}
              className="shrink-0"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Quick Actions - Only show when not in manage mode */}
            {!isManageMode && (
              <>
                {/* Resume button for failed/cancelled/paused/queued states */}
                {(study.status === "failed" ||
                  study.status === "cancelled" ||
                  study.status === "paused") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onResume();
                    }}
                    disabled={isResuming}
                    className="h-8 px-2 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                    title="Resume study (continue from last trial)"
                  >
                    {isResuming ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>
                )}

                {/* Delete button for failed/completed/cancelled states or paused */}
                {(study.status === "failed" ||
                  study.status === "completed" ||
                  study.status === "cancelled" ||
                  study.status === "paused") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                    className="h-8 px-2 text-red-400 hover:text-red-300"
                    title="Delete study"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}

                {/* Start button for slot-waiting studies (queued with no job_id) */}
                {isSlotWaiting(study) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onStart();
                    }}
                    disabled={isStarting || allFrozen}
                    className="h-8 px-2 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                    title={allFrozen ? "Processing is temporarily paused for a system update." : "Start study now"}
                  >
                    {isStarting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>
                )}

                {/* Cancel button for queued studies */}
                {study.status === "queued" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStop();
                    }}
                    className="h-8 px-2 text-red-400 hover:text-red-300"
                    title="Cancel study"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}

                {/* Pause/Stop button for running studies */}
                {study.status === "running" && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPause();
                      }}
                      className="h-8 px-2 text-slate-400 hover:text-slate-300"
                      title="Pause study"
                    >
                      <Pause className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStop();
                      }}
                      className="h-8 px-2 text-red-400 hover:text-red-300"
                      title="Cancel study"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                )}

                {/* Delete button for queued studies */}
                {study.status === "queued" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                    className="h-8 px-2"
                    title="Delete study"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}

            <span
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium capitalize",
                "bg-dark-800/50 backdrop-blur-sm border",
                study.status === "running" &&
                  "border-blue-500/50 text-blue-400",
                study.status === "completed" &&
                  "border-green-500/50 text-green-400",
                study.status === "failed" && "border-red-500/50 text-red-400",
                study.status === "queued" &&
                  "border-yellow-500/50 text-yellow-400",
                study.status === "paused" &&
                  "border-slate-500/50 text-slate-400"
              )}
            >
              {study.status}
            </span>


            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="h-8 px-2"
              title="Show details"
            >
              <Eye className="h-4 w-4" />
            </Button>

            {/* Open in new tab button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/results/${study.study_id}`, '_blank');
              }}
              className="h-8 px-2"
              title="Open results in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </GlassCardHeader>

      <GlassCardContent className="space-y-3 relative pt-0 pb-3">
        {/* Progress Section */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-300">
                Progress
              </span>
              {study.status === "running" && (
                <motion.div
                  className="flex items-center gap-1.5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Continuous rotating indicator */}
                  <div className="relative w-3 h-3">
                    <motion.div className="absolute inset-0 rounded-full border border-blue-400/30" />
                    <motion.div
                      className="absolute inset-0 rounded-full border-t border-blue-400"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                  </div>
                  <span className="text-xs text-blue-400">
                    Running{study.worker_id && <> on <span className="font-mono">{study.worker_id}</span></>}
                  </span>
                </motion.div>
              )}
              {study.status === "failed" && (
                <motion.div
                  className="flex items-center gap-1"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-red-500 ml-1">Failed</span>
                </motion.div>
              )}
            </div>
            <span className="font-mono text-sm font-semibold bg-dark-800/50 px-2 py-1 rounded-md">
              {resolvedCurrentTrial} / {resolvedTotalTrials}
              <span className="text-xs text-muted-foreground ml-1">trials</span>
            </span>
          </div>
          <div className="relative px-1">
            <Progress
              value={progress}
              animate={study.status === "running" && progress < 100}
              variant={
                study.status === "completed" || progress >= 100
                  ? "success"
                  : study.status === "failed"
                    ? "danger"
                    : study.status === "paused"
                      ? "muted"
                      : "brand"
              }
              className="h-2.5"
            />
          </div>
          {study.status === "running" && estimatedMinutesRemaining !== null && (
            <motion.div
              className="flex items-center gap-2 text-xs"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">
                Estimated time remaining:
                <span className="font-medium text-gray-300 ml-1">
                  ~{estimatedMinutesRemaining} min
                </span>
              </span>
            </motion.div>
          )}
        </div>

        {/* Stats Grid */}
        <div className={cn(
          "grid grid-cols-2 gap-x-4 gap-y-2 text-sm",
          study.status === "completed" ? "md:grid-cols-3 lg:grid-cols-6" : "md:grid-cols-4"
        )}>
          <div className="space-y-0.5">
            <p className="text-muted-foreground text-xs">
              {/* AIE-403: label adapts to the study's chosen objective_function */}
              Best Value ({objectiveLabel(study.objective_function)})
            </p>
            <p className="font-mono font-semibold">
              {/* AIE-319: append approx real Sortino/Sharpe alongside the strict best */}
              {formatStrictWithApprox(
                bestValue,
                study.total_trades,
                parseApproxConfigFromToml(study.toml_config),
                { strictDigits: 6 },
              )}
            </p>
          </div>

          <div className="space-y-0.5">
            <p className="text-muted-foreground text-xs">Symbol</p>
            <p className="font-medium">
              {(() => {
                // Check if study has symbol field
                const symbol = study.symbol;
                if (symbol) {
                  return symbol;
                }

                // Fallback: extract filename from study_name
                if (study.study_name) {
                  // Try to extract filename from study name patterns like:
                  // "filename.csv_optimization" or "optimization_filename.csv" etc.
                  const csvMatch = study.study_name.match(/([^\/\\]+\.csv)/i);
                  if (csvMatch) {
                    return csvMatch[1];
                  }

                  // Try to extract other common filename patterns
                  const fileMatch = study.study_name.match(
                    /([^\/\\]+\.[a-zA-Z]{2,4})/
                  );
                  if (fileMatch) {
                    return fileMatch[1];
                  }

                  // If no file extension found, try to find parts that look like filenames
                  const parts = study.study_name.split(/[_\-\s]+/);
                  for (const part of parts) {
                    // Look for parts that don't look like status words
                    if (
                      part &&
                      !["optimization", "study", "trial"].includes(
                        part.toLowerCase()
                      )
                    ) {
                      return part;
                    }
                  }
                }

                return "N/A";
              })()}
            </p>
          </div>

          <div className="space-y-0.5">
            <p className="text-muted-foreground text-xs">Created</p>
            <p
              className="font-medium"
              title={
                study.created_at
                  ? formatLocalDate(study.created_at, DateFormats.FULL)
                  : undefined
              }
            >
              {formatRelativeTime(study.created_at)}
            </p>
          </div>

          {/* Throughput — always shown */}
          <div className="space-y-0.5">
            <p className="text-muted-foreground text-xs">Throughput</p>
            <p className="font-medium">
              {(() => {
                // Priority: SSE ref > API enrichment > computed fallback
                const tps = study.status === "completed"
                  ? (trialsPerSecond ?? study.trials_per_second ?? (() => {
                      // Use || not ?? so persisted 0 falls through to n_trials
                      const nTrials = study.n_trials_completed || study.n_trials;
                      const duration = study.optimization_duration_seconds
                        ?? calculateDurationSeconds(study.created_at, study.completed_at);
                      return (nTrials && duration && duration > 0) ? nTrials / duration : null;
                    })())
                  : trialsPerSecond;
                if (tps != null && tps > 0) {
                  return (
                    <>
                      {tps.toFixed(2)}
                      <span className="text-xs text-muted-foreground ml-1">
                        trials/s
                      </span>
                    </>
                  );
                }
                return "N/A";
              })()}
            </p>
          </div>

          {/* Duration — completed studies only */}
          {study.status === "completed" && (
            <div className="space-y-0.5">
              <p className="text-muted-foreground text-xs">Duration</p>
              <p className="font-medium">
                {formatDuration(
                  study.optimization_duration_seconds
                    ?? calculateDurationSeconds(study.created_at, study.completed_at)
                )}
              </p>
            </div>
          )}

          {/* Completed — completed studies only */}
          {study.status === "completed" && (
            <div className="space-y-0.5">
              <p className="text-muted-foreground text-xs">Completed</p>
              <p
                className="font-medium"
                title={
                  study.completed_at
                    ? formatLocalDate(study.completed_at, DateFormats.FULL)
                    : undefined
                }
              >
                {study.completed_at
                  ? formatLocalDate(study.completed_at, DateFormats.COMPACT)
                  : "N/A"}
              </p>
            </div>
          )}
        </div>

        {/* Error Display */}
        {study.error_message && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-3 py-2 rounded-lg"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Error Details:</p>
                <p className="text-xs mt-1 break-all">{study.error_message}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Expanded Details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3 pt-3 border-t border-dark-700"
            >
              {/* Study Configuration */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Study Configuration:</p>
                <div className="bg-dark-800/50 p-3 rounded-lg space-y-2 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-muted-foreground">
                        Total Trials:
                      </span>
                      <span className="ml-2 font-mono">
                        {study.n_trials || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Symbol:</span>
                      <span className="ml-2 font-mono">
                        {study.symbol || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Timeframe:</span>
                      <span className="ml-2 font-mono">
                        {study.timeframe || "N/A"}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Study ID:</span>
                      <span className="ml-2 font-mono">{study.study_id}</span>
                    </div>
                  </div>
                  {study.parameters && (
                    <div className="mt-3 pt-3 border-t border-dark-700">
                      <p className="text-muted-foreground mb-2">Parameters:</p>
                      <pre className="text-xs overflow-x-auto">
                        {JSON.stringify(study.parameters, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>

              {/* Best Parameters */}
              {study.best_params && (
                <div>
                  <p className="text-sm font-medium mb-2">Best Parameters:</p>
                  <pre className="text-xs bg-dark-800/50 p-3 rounded-lg overflow-x-auto">
                    {JSON.stringify(study.best_params, null, 2)}
                  </pre>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-dark-800/50 border-dark-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View in Dashboard
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </GlassCardContent>
    </GlassCard>
  );
}
